import KPIDataSummary from '@modules/order/services/KPIDataSummary'
import { CancellableRequest } from '@services/HttpService'
import { nav } from '@stores/nav'
import {
  HIERARCHY_API_KEY,
  PaginatedHierarchyItemsDataStore
} from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import join from 'lodash/join'
import map from 'lodash/map'
import { action, computed, observable, reaction, toJS } from 'mobx'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { KPITableView } from './KPIViewTableService'
import {
  CATEGORY_UI_SEPERATOR,
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  KPI_TOPBAR_METRIC_ATTRIBUTES,
  METRIC_ATTRIBUTES_KPI_BUY,
  METRIC_ATTRIBUTES_KPI_FORECAST,
  METRIC_ATTRIBUTES_WHOLESALE
} from '@modules/order/constants/uiConstants'
import { stores } from '@stores'
import {
  addParentsGroupData,
  removeSkippedGroups
} from '@modules/order/utils/dynamic-group-mapping'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import { orderKPIGlobalTopBarStore } from '../../OrderKPIGlobalTopBarStore'
import { View } from '@modules/order/constants/views'

export class KPIDataStore extends PaginatedHierarchyItemsDataStore {
  private overviewReactions = []
  @observable.shallow hierarchyWithoutFilters = []
  @observable quantityType = ''
  @observable planningBudgetHeirarchy
  @observable totalDoorKPIs = []

  private service = new KPIDataSummary()

  @computed get isEnhancedViewEnabled() {
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    return enhancedViewEnabled
  }

  @computed get activities() {
    const hierarchy = map(this.hierarchyWithoutFilters, 'name')
    return join(hierarchy, CATEGORY_UI_SEPERATOR)
  }

  fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number
  ): CancellableRequest<any> | Promise<any[]> {
    return Promise.resolve([])
  }

  fetchHierarchyFromAPI(): CancellableRequest<any> | Promise<any> {
    if (this.isEnhancedViewEnabled) {
      return this.fetchHierarchyNewFromAPI().then(resp => {
        const { heirarchy, totalDoorHierarchyKPIs, orderTopbarKPIs } = resp?.data
        this.processNewHierarchy(heirarchy)
        totalDoorHierarchyKPIs && this.processNewHierarchy(totalDoorHierarchyKPIs)
        this.totalDoorKPIs = totalDoorHierarchyKPIs?.children ?? []
        const topbarData = {
          ...orderTopbarKPIs,
          totalProducts:
            orderTopbarKPIs.totalProducts ?? orderTopbarKPIs.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        return heirarchy?.children
      })
    } else {
      return this.fetchHierarchyOldFromAPI()
    }
  }

  fetchHierarchyNewFromAPI = () => {
    const {
      queryParams: {
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        clientId,
        clientType,
        buyingSession,
        subSection
      }
    } = nav
    return VIPClientType[clientType] === VIPClientType.Internal
      ? this.getHeirarchyForRetail(
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          subSection
        )
      : this.getHeirarchyForWholesale(
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientId,
          buyingSession,
          subSection
        )
  }
  fetchHierarchyOldFromAPI = () => {
    const {
      queryParams: {
        buyingSessionGroupId,
        orderId,
        orderType,
        subSection,
        clientOrderId,
        clientId,
        clientType,
        buyingSession
      }
    } = nav
    return VIPClientType[clientType] === VIPClientType.Internal
      ? buyingSession
        ? subSection === KPITableView.Forecast
          ? this.service.fetchForecastKPIHierarchy(
              buyingSessionGroupId,
              buyingSession,
              clientId
            )
          : this.service.fetchBuyKPIHierarchy(
              buyingSessionGroupId,
              buyingSession,
              clientId,
              this.quantityType
            )
        : null
      : this.service.fetchHierarchy(
          buyingSessionGroupId,
          orderId,
          orderType,
          subSection,
          clientOrderId
        )
  }
  fetchFilterAttributesFromAPI(): CancellableRequest<any> | Promise<any> {
    return Promise.resolve([])
  }

  removeItemAPI(productId: string): Promise<any> {
    return null
  }

  getHeirarchyForRetail = (
    buyingSessionGroupId: string,
    orderId: string,
    orderType: string,
    clientOrderId: string,
    subSection: string
  ) => {
    const body = {
      heirarchy: {
        sort: {},
        filters: {},
        excludeFilters: {},
        groupBy: [],
        metricAttributes:
          subSection === KPITableView.Forecast
            ? METRIC_ATTRIBUTES_KPI_FORECAST
            : METRIC_ATTRIBUTES_KPI_BUY
      },
      orderTopbarKPIs: {
        metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      }
    }
    return this.service.fetchEnhancedOrderProductHeirarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      clientOrderId,
      body
    )
  }

  getHeirarchyForWholesale = (
    buyingSessionGroupId: string,
    orderId: string,
    orderType: string,
    clientOrderId: string,
    clientId: string,
    buyingSession: string,
    subSection: string
  ) => {
    const body = {
      heirarchy: {
        sort: {},
        filters: {},
        excludeFilters: {},
        groupBy: [],
        metricAttributes: METRIC_ATTRIBUTES_WHOLESALE
      },
      totalDoorHierarchyKPIs: {
        sort: {},
        filters: {},
        excludeFilters: {},
        preAdditionalDefaultGroupBy: [
          {
            attributeId: 'doorId',
            orderBy: 'asc'
          }
        ],
        metricAttributes: METRIC_ATTRIBUTES_WHOLESALE
      },
      orderTopbarKPIs: {
        metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      }
    }
    return this.getPlanningHeirarchyForWholesale(
      buyingSessionGroupId,
      clientId,
      buyingSession,
      subSection,
      orderType
    ).then(resp => {
      const temp = resp?.data
      removeSkippedGroups(temp)
      addParentsGroupData(temp)
      this.planningBudgetHeirarchy = temp?.children?.[0]?.children

      return this.service.fetchEnhancedOrderProductHeirarchy(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
    })
  }

  getPlanningHeirarchyForWholesale = (
    buyingSessionGroupId: string,
    clientId: string,
    buyingSession: string,
    subSection: string,
    orderType: string
  ) => {
    const grouping =
      subSection === KPITableView.Overview && !(orderType === OrderType.Door)
        ? [
            {
              attributeId: 'buyer',
              orderBy: 'A_TO_Z'
            }
          ]
        : [
            {
              attributeId: 'buyer',
              orderBy: 'A_TO_Z'
            },
            {
              attributeId: 'location',
              orderBy: 'A_TO_Z'
            }
          ]
    const body = {
      preAdditionalDefaultGroupBy: grouping,
      metricAttributes: [
        'totalBudgetQuantity',
        'buyerBudgetValue',
        'mixOfBuyerBudgetValue',
        'mixOfBudgetQuantity'
      ],
      buyingSessionId: buyingSession,
      useExchangeRateForBuyingSessionGroupId: buyingSessionGroupId,
      usePlanningBudgetsWithNoBuyer: true
    }

    return this.service.fetchEnhancedPlanningBudgetHeirarchy(
      buyingSessionGroupId,
      clientId,
      body
    )
  }

  replaceDataServiceWith(service) {
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.itemIndexObjectStore.clearAll()
    this.heirarchy = []
    this.service = service
  }

  @action
  setQuantityType = (type: string) => {
    this.quantityType = type
  }

  @action
  async init() {
    if (this.isEnhancedViewEnabled) {
      const {
        nav: {
          queryParams: { subSection, view }
        }
      } = stores
      if (view === View.KPI && subSection && !KPITableView[subSection]) {
        // Do not call heirarchy API for custom view
        this.heirarchyLoading = false
        return
      }
    }
    if (this.quantityType || this.isEnhancedViewEnabled) {
      await super.init()
      this.overviewReactions.push(
        reaction(
          () => this.heirarchy,
          () => {
            if (!this.filterStore.appliedFilters?.length && !this.searchKey) {
              this.hierarchyWithoutFilters = toJS(this.heirarchy, {
                recurseEverything: true
              })
            }
          }
        )
      )
    } else {
      this.heirarchyLoading = false
    }
  }

  @action
  destroy() {
    super.destroy()
    this.overviewReactions.forEach(disposer => disposer())
    this.overviewReactions = []
    this.setQuantityType('')
  }
}
